import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import WatchLaterRoundedIcon from "@mui/icons-material/WatchLaterRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseIcon from "@mui/icons-material/Close";

import { primary, gray, success, warning } from "../../theme/colors";

type CoiStatus = "pending" | "in_progress" | "completed";

interface CoiDocumentUploadBannerProps {
  status: CoiStatus;
  onUploadClick: () => void;
  onDismiss?: () => void;
}

const copy: Record<CoiStatus, { title: string; desc: string }> = {
  pending: {
    title: "Fast-track your project — upload your Certificate of Insurance early",
    desc: "Projects with a COI on file move through utility interconnection approval about 2× faster. Add yours now to avoid a delay later.",
  },
  in_progress: {
    title: "Certificate of Insurance — pending verification",
    desc: "Thanks! Our team is reviewing your COI against your utility’s requirements. We’ll update your project status once it’s verified.",
  },
  completed: {
    title: "Certificate of Insurance verified",
    desc: "Your COI has been verified and attached to your project. This step is complete — no further action needed.",
  },
};

export const CoiDocumentUploadBanner = ({
  status,
  onUploadClick,
  onDismiss,
}: CoiDocumentUploadBannerProps) => {
  const isCompleted = status === "completed";
  const iconColor = isCompleted
    ? success[600]
    : status === "in_progress"
    ? warning[600]
    : primary[400];

  return (
    <Paper
      sx={{
        border: `1px solid ${gray[100]}`,
        minHeight: "76px",
        padding: 4,
        borderRadius: { xs: 0, sm: "8px" },
      }}
    >
      <Stack
        sx={{
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
          gap: { xs: "16px", sm: "30px" },
        }}
        height="100%"
      >
        <Stack
          direction="row"
          gap="15px"
          alignItems="center"
          flex="auto"
        >
          {isCompleted ? (
            <CheckCircleRoundedIcon sx={{ color: iconColor }} />
          ) : (
            <WatchLaterRoundedIcon sx={{ color: iconColor }} />
          )}
          <Stack gap="3px">
            <Typography
              fontWeight={600}
              sx={{ whiteSpace: { xs: "normal", sm: "normal" } }}
              fontSize="16px"
              color={gray[800]}
            >
              {copy[status].title}
            </Typography>
            <Typography variant="body2" color={gray[700]}>
              {copy[status].desc}
            </Typography>
          </Stack>
        </Stack>
        {status === "pending" && (
          <Button
            variant="outlined"
            size="small"
            onClick={onUploadClick}
            sx={{
              backgroundColor: gray[100],
              borderColor: gray[100],
              color: gray[700],
              fontWeight: 500,
              textTransform: "none",
              whiteSpace: "nowrap",
              flexShrink: 0,
              "&:hover": {
                backgroundColor: gray[200],
                borderColor: gray[200],
              },
            }}
          >
            Upload COI
          </Button>
        )}
        {status === "in_progress" && (
          <Chip
            label="In review"
            sx={{
              backgroundColor: gray[100],
              color: gray[700],
              fontWeight: 500,
              borderRadius: "8px",
            }}
          />
        )}
        {isCompleted && onDismiss && (
          <IconButton
            color="inherit"
            size="small"
            onClick={onDismiss}
            aria-label="dismiss"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Stack>
    </Paper>
  );
};
